import React from 'react';
import {
  Button,
  ButtonType,
  Input,
  Select
} from 'syngenta-digital-cropwise-react-ui-kit';
import ChevronDownIcon from 'assets/chevron_down.svg?react';
import ChevronUpIcon from 'assets/chevron_up.svg?react';
import OpenIcon from 'assets/open_in_new.svg?react';
import FormSectionHeader from 'screens/Applications&WorkOrders/StepsLayout/Components/ProductsStep/components/FillProductDetailsDrawer/components/FormSectionHeader';
import {
  getAbbreviatedUnit,
  getShortUnitLabel
} from 'helpers/unitsAbbreviation';

export interface ProductQuantitySectionProps {
  totalQuantityInput: string;
  totalQuantityUnit: string;
  units: Array<{ key: string }>;
  qtyError: string;
  unitError: string;
  isUnitDropdownOpen: boolean;
  setIsUnitDropdownOpen: (open: boolean) => void;
  onQuantityChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onQuantityFocus: () => void;
  onQuantityBlur: () => void;
  onUnitChange: (unit: string) => void;
  labelDocument: { id: string; description: string } | null;
  openFile: (id: string) => void;
}

/**
 * Presentational half of the drawer's Product Quantity section. Holds no state
 * of its own — every value and handler comes from the drawer, which keeps the
 * interdependent quantity/price/cost arithmetic in one place.
 */
const ProductQuantitySection: React.FC<ProductQuantitySectionProps> = ({
  totalQuantityInput,
  totalQuantityUnit,
  units,
  qtyError,
  unitError,
  isUnitDropdownOpen,
  setIsUnitDropdownOpen,
  onQuantityChange,
  onQuantityFocus,
  onQuantityBlur,
  onUnitChange,
  labelDocument,
  openFile
}) => (
  <section className="mb-6">
    <FormSectionHeader title="Product Quantity" />
    <div className="mt-3 cwf-invoice-product-drawer-form-content">
      <Input
        label="Total Invoiced Quantity"
        type="text"
        autoFocus
        required
        value={totalQuantityInput}
        onChange={onQuantityChange}
        onFocus={onQuantityFocus}
        onBlur={onQuantityBlur}
        suffix={
          totalQuantityUnit ? getAbbreviatedUnit(totalQuantityUnit) : undefined
        }
        status={qtyError ? 'error' : undefined}
        data-testid="fill-product-quantity"
        addonAfter={
          <Select
            showSearch
            placeholder="Select unit"
            value={totalQuantityUnit || undefined}
            onChange={onUnitChange}
            className="custom-select"
            status={unitError ? 'error' : undefined}
            data-testid="fill-product-unit"
            suffixIcon={
              isUnitDropdownOpen ? (
                <ChevronUpIcon width={18} height={18} />
              ) : (
                <ChevronDownIcon width={18} height={18} />
              )
            }
            onDropdownVisibleChange={setIsUnitDropdownOpen}
            filterOption={(input: string, option: any) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          >
            {units.map(unit => (
              <Select.Option
                key={unit.key}
                value={unit.key}
                label={getShortUnitLabel(unit.key)}
              >
                {getShortUnitLabel(unit.key)}
              </Select.Option>
            ))}
          </Select>
        }
      />
      {qtyError && <p className="text-red-50 text-xs mt-1">{qtyError}</p>}
      {unitError && <p className="text-red-50 text-xs mt-1">{unitError}</p>}
    </div>

    {/* FR-108: Open Product Label at the bottom of the Product Quantity
          section, matching the Applications drawer (placement + behavior). */}
    {labelDocument && (
      <Button
        className="w-full flex justify-center items-center mt-4"
        type={ButtonType.primary}
        icon={<OpenIcon />}
        ghost
        onClick={() => openFile(labelDocument.id)}
      >
        Open Product Label
      </Button>
    )}
  </section>
);

export default ProductQuantitySection;
